import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { MetadataFillingProposalPatched } from '@iconik-io/iconik-api/src/schema_patched/metadata_filling';

import { getAiMetadataGenerationKey } from './selectors';
import type { AiMetadataAppState, AiMetadataBulkReviewAction } from './types';

type AssetVersionPayload = {
  assetId: string;
  versionId: string;
};

type GenerationStartedPayload = AssetVersionPayload & {
  viewId: string;
  selectedFields: string[];
  autoAccept?: boolean;
};

type GenerationJobIdSetPayload = AssetVersionPayload & {
  jobId: string;
};

type GenerationSucceededPayload = AssetVersionPayload & {
  response: MetadataFillingProposalPatched;
};

type GenerationFailedPayload = AssetVersionPayload & {
  errorMessage: string;
};

const initialState: AiMetadataAppState = {
  generations: {},
  bulkReviewRequests: {},
};

export const aiMetadataSlice = createSlice({
  name: 'ai_metadata',
  initialState,
  reducers: {
    aiMetadataGenerationStarted: (
      state,
      action: PayloadAction<GenerationStartedPayload>,
    ) => {
      const { assetId, versionId, viewId, selectedFields, autoAccept } =
        action.payload;
      state.generations[getAiMetadataGenerationKey(assetId, versionId)] = {
        assetId,
        versionId,
        status: 'pending',
        viewId,
        selectedFields,
        autoAccept,
      };
    },
    aiMetadataGenerationJobIdSet: (
      state,
      action: PayloadAction<GenerationJobIdSetPayload>,
    ) => {
      const { assetId, versionId, jobId } = action.payload;
      const existing =
        state.generations[getAiMetadataGenerationKey(assetId, versionId)];
      if (!existing) {
        return;
      }
      existing.jobId = jobId;
    },
    aiMetadataGenerationSucceeded: (
      state,
      action: PayloadAction<GenerationSucceededPayload>,
    ) => {
      const { assetId, versionId, response } = action.payload;
      const existing =
        state.generations[getAiMetadataGenerationKey(assetId, versionId)];
      if (!existing) {
        return;
      }
      existing.status = 'success';
      existing.response = response;

      // Backfill the view and field selection
      if (!existing.viewId && response.view_id) {
        existing.viewId = response.view_id;
      }
      if (
        existing.selectedFields.length === 0 &&
        response.field_names?.length
      ) {
        existing.selectedFields = response.field_names;
      }
    },
    aiMetadataGenerationFailed: (
      state,
      action: PayloadAction<GenerationFailedPayload>,
    ) => {
      const { assetId, versionId, errorMessage } = action.payload;
      const existing =
        state.generations[getAiMetadataGenerationKey(assetId, versionId)];
      if (!existing) {
        return;
      }
      existing.status = 'error';
      existing.errorMessage = errorMessage;
    },
    aiMetadataGenerationCleared: (
      state,
      action: PayloadAction<AssetVersionPayload>,
    ) => {
      const { assetId, versionId } = action.payload;
      delete state.generations[getAiMetadataGenerationKey(assetId, versionId)];
    },
    aiMetadataGenerationPendingModalOpenSet: (
      state,
      action: PayloadAction<AssetVersionPayload>,
    ) => {
      const { assetId, versionId } = action.payload;
      const existing =
        state.generations[getAiMetadataGenerationKey(assetId, versionId)];
      if (existing) {
        existing.pendingModalOpen = true;
      }
    },
    aiMetadataGenerationPendingModalOpenCleared: (
      state,
      action: PayloadAction<AssetVersionPayload>,
    ) => {
      const { assetId, versionId } = action.payload;
      const existing =
        state.generations[getAiMetadataGenerationKey(assetId, versionId)];
      if (existing) {
        existing.pendingModalOpen = false;
      }
    },
    bulkReviewRequestStarted: (
      state,
      action: PayloadAction<{
        parentJobId: string;
        status: AiMetadataBulkReviewAction;
      }>,
    ) => {
      const { parentJobId, status } = action.payload;
      state.bulkReviewRequests[parentJobId] = { status, phase: 'submitting' };
    },
    bulkReviewRequestSucceeded: (state, action: PayloadAction<string>) => {
      const request = state.bulkReviewRequests[action.payload];
      if (request) {
        request.phase = 'submitted';
      }
    },
    bulkReviewRequestFailed: (state, action: PayloadAction<string>) => {
      delete state.bulkReviewRequests[action.payload];
    },
  },
});

export const {
  aiMetadataGenerationStarted,
  aiMetadataGenerationJobIdSet,
  aiMetadataGenerationSucceeded,
  aiMetadataGenerationFailed,
  aiMetadataGenerationCleared,
  aiMetadataGenerationPendingModalOpenSet,
  aiMetadataGenerationPendingModalOpenCleared,
  bulkReviewRequestStarted,
  bulkReviewRequestSucceeded,
  bulkReviewRequestFailed,
} = aiMetadataSlice.actions;

export const aiMetadataReducer = aiMetadataSlice.reducer;
